/**
 * @author MrZenW
 * @email MrZenW@Gmail.com, https://MrZenW.com
 * @create date 2021-05-25 13:20:12
 * @modify date 2022-04-29 16:06:04
 * @desc [description]
 */
const FingerprintJS = require('@fingerprintjs/fingerprintjs');
const Httpif = require('$/libraries/httpif');
const { statehookify, parsePath } = require('$/statehook');
const { digest } = require('$/libraries/ezdigest');
const { grabLocalStorageService } = require('./local_storage_service');

const fpPromise = FingerprintJS.load();

const defaultConfig = {
  baseURL: process.env.API_BASE_URL,
  timeout: 5 * 1e3,
  headers: { Accept: 'application/json' },
};
const httpifClient = Httpif.create(defaultConfig);

module.exports = statehookify({ statehookName: 'config_service', bindThisToBase: true }, {
  CONT__ROBOT_POSITION_SENDING_AND_RECEIVING_TIME_GAP: 1000 / 10,
  get localStorageService() {
    return this.statehookGetPath('localStorageService');
  },
  get unityStaticFolder() {
    return this.getConfigItem('unityStaticFolder');
  },
  set unityStaticFolder(newValue) {
    return this.setConfigItem('unityStaticFolder', newValue);
  },
  setConfigItem(name, value) {
    const locaStoreKey = parsePath(name, '/').join('/');
    this.localStorageService.setLocalItem(locaStoreKey, value);
    return this.statehookSetPath(name, value);
  },
  getConfigItem(name) {
    const localStorageValue = this.localStorageService.getLocalItem(name);
    if (this.statehookGetPath(name) !== localStorageValue) {
      this.statehookSetPath(name, localStorageValue);
    }
    return localStorageValue;
  },
  async fetchClientInfo() {
    const headers = {};
    const clientId = this.getConfigItem('clientId');
    if (clientId) {
      const zAuth = new URLSearchParams({
        clid: clientId,
      });
      headers['Authorization'] = 'zAuth ' + zAuth.toString();
    }
    const fp = await fpPromise;
    const result = await fp.get();
    const fpDigest = digest(result.visitorId).string;
    const search = {
      fp_digest: fpDigest,
      fp: result.visitorId,
    };
    return httpifClient.get('/client', search, null, { headers })
      .then((res) => {
        const { data } = res;
        this.setConfigItem('clientId', data.clientDoc.clientId);
        return res;
      });
  },
  fetchSessionInfo(sessionId) {
    const headers = {};
    const clientId = this.getConfigItem('clientId');
    if (clientId && sessionId) {
      const zAuth = new URLSearchParams({
        clid: clientId,
        sessid: sessionId,
      });
      headers['Authorization'] = 'zAuth ' + zAuth.toString();
    }
    return httpifClient.get('/client/session/info', null, null, { headers })
      .then((resp) => {
        return resp.data.sessionDoc;
      });
  },
  fetchNewSession() {
    const headers = {};
    const clientId = this.getConfigItem('clientId');
    if (clientId) {
      const zAuth = new URLSearchParams({
        clid: clientId,
      });
      headers['Authorization'] = 'zAuth ' + zAuth.toString();
    }
    return httpifClient.get('/client/session/generate', null, null, { headers })
      .then((resp) => {
        return resp.data.session;
      });
  },
  grabSessionInfo(sessionId) {
    if (sessionId) return this.fetchSessionInfo(sessionId);
    return this.fetchNewSession();
  },
  setRoomEntranceCode(roomCode, entranceCode) {
    return this.setConfigItem(['room_entrance_code', roomCode], entranceCode);
  },
  getRoomEntranceCode(roomCode) {
    return this.getConfigItem(['room_entrance_code', roomCode]);
  },
  setRoomEntranceStudentName(roomCode, studentName) {
    return this.setConfigItem(['room_entrance_student_name', roomCode], studentName);
  },
  getRoomEntranceStudentName(roomCode) {
    return this.getConfigItem(['room_entrance_student_name', roomCode]);
  },
  get currentSessionId() {
    return this.getConfigItem('currentSessionId');
  },
  set currentSessionId(sessionId) {
    return this.setConfigItem('currentSessionId', sessionId);
  },
  get connectedSessionId() {
    return this.getConfigItem('connectedSessionId');
  },
  set connectedSessionId(sessionId) {
    return this.setConfigItem('connectedSessionId', sessionId);
  },
  connectSession() {
    const clientId = this.getConfigItem('clientId');
    if (clientId && this.currentSessionId && this.connectedSessionId !== this.currentSessionId) {
      const zAuth = new URLSearchParams({
        clid: clientId,
        sessid: this.currentSessionId,
      });
      const headers = { Authorization: 'zAuth ' + zAuth.toString() };
      return httpifClient.post('/client/session/connect', null, null, { headers })
        .then(() => {
          this.connectedSessionId = this.currentSessionId;
          return 'OK';
        });
    }
    return '';
  },
  disconnectSession() {
    const clientId = this.getConfigItem('clientId');
    if (clientId && this.connectedSessionId) {
      // Use sendBeacon to end user sessions so that it executes correctly on page unload
      navigator.sendBeacon(`${process.env.API_BASE_URL}/client/session/disconnect`, JSON.stringify({ clid: clientId }));
      this.connectedSessionId = null;
      return 'OK';
    }
    return '';
  },
  postSessionDevice(deviceId, macAddress, firmwareVersion, cardCount) {
    if (!this.connectedSessionId) return Promise.resolve('No session connected');
    if (!deviceId) return Promise.resolve('No deviceId');
    if (!macAddress) return Promise.resolve('No macAddress');
    if (!firmwareVersion) return Promise.resolve('No firmwareVersion');
    if (typeof cardCount !== 'number') return Promise.resolve('No cardCount');
    const clientId = this.getConfigItem('clientId');
    if (!clientId) return Promise.resolve('No clientId');
    const zAuth = new URLSearchParams({
      clid: clientId,
      sessid: this.connectedSessionId,
    });
    const headers = { Authorization: 'zAuth ' + zAuth.toString() };
    // console.log(`Sending RobotDetails: ${deviceId}, ${macAddress}, ${firmwareVersion}, ${cardCount}`);
    const payload = {
      'uid': deviceId,
      'ma': macAddress,
      'version': firmwareVersion,
      'cc': cardCount,
      'time': Date.now(),
    };
    return httpifClient.post('/client/session/device', null, payload, { headers })
      .then((resp) => {
        return resp.data;
      });
  },
}, (thisStatehook, baseObject) => {
  const localStorageService = grabLocalStorageService('global_config_service');
  thisStatehook.statehookSetPath('localStorageService', localStorageService);
  // localStorageService.setLocalItem({
  //   unityStaticFolder: localStorageService.getLocalItem('unityStaticFolder') || '/static/public/MicrodotTesting',
  // });
  baseObject.unityStaticFolder = localStorageService.getLocalItem('unityStaticFolder') || process.env.UNITY_ASSETS_URL || '/static/public/MicrodotTesting';
  return () => {
    localStorageService.statehookDiscard();
  };
});
